<template>
    <div v-if="isDoctor" class="grid gap-4 lg:grid-cols-2">
        <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm p-5 space-y-4">
            <div class="flex items-start justify-between space-x-2">
                <h2 class="text-lg font-semibold text-gray-700">
                    Welcome<template v-if="profile">, {{ profile.title }} {{ profile.full_name }}</template>
                </h2>
                <UBadge v-if="profile" :color="statusColor[profile.status]" variant="subtle" class="capitalize">{{ profile.status }}</UBadge>
            </div>

            <USkeleton v-if="loading" class="h-16 w-full" />
            <p v-else-if="!profile" class="text-sm text-gray-500">Your doctor profile could not be found. Contact SAMU support.</p>
            <template v-else>
                <div v-if="gaps.doctor.length === 0" class="flex items-center space-x-2 text-sm text-green-700">
                    <UIcon name="i-ph-check-circle-duotone" class="size-5" />
                    <span>Your profile is complete</span>
                </div>
                <div v-else class="space-y-2">
                    <p class="text-sm font-medium text-gray-600">Complete your profile:</p>
                    <ul class="list-disc pl-5 text-sm text-gray-500 space-y-1">
                        <li v-for="blocker in gaps.doctor" :key="blocker">{{ blocker }}</li>
                    </ul>
                </div>
                <p v-if="gaps.team.length" class="text-sm text-gray-500">Waiting on the telemedicine team: {{ gaps.team.join(', ') }}</p>
                <UButton to="/telemedicine/profile" color="black" icon="i-ph-identification-card-duotone">Open my profile</UButton>
            </template>
        </div>

        <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm p-5 space-y-2">
            <h2 class="text-lg font-semibold text-gray-700">Coming soon</h2>
            <p class="text-sm text-gray-500">Schedule & consultations arrive in the next release.</p>
        </div>
    </div>

    <div v-else class="space-y-4">
        <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div v-for="stat in stats" :key="stat.label" class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm p-5">
                <p class="text-sm text-gray-500">{{ stat.label }}</p>
                <p class="mt-1 text-3xl font-semibold text-gray-700">{{ stat.value }}</p>
            </div>
        </div>

        <div class="grid gap-4 lg:grid-cols-2">
            <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm p-5 space-y-3">
                <h2 class="text-lg font-semibold text-gray-700">Service status</h2>
                <USkeleton v-if="loading" class="h-6 w-28" />
                <UBadge v-else :color="enabled ? 'green' : 'gray'" variant="subtle">{{ enabled ? 'Live in app' : 'Hidden in app' }}</UBadge>
            </div>

            <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm p-5 space-y-3">
                <h2 class="text-lg font-semibold text-gray-700">Next up</h2>
                <ul class="space-y-2 text-sm text-gray-500">
                    <li v-for="milestone in roadmap" :key="milestone.code">
                        <span class="font-medium text-gray-700">{{ milestone.code }}</span> {{ milestone.text }}
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'
import { DoctorStatus, type DoctorProfile } from '~/types/telemedicine'
import { publishGaps } from '~/utils/telemedicineDoctor'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin, UserRole.telemedicineAdmin, UserRole.telemedicineDoctor] })

const client = useSupabaseClient()
const auth = useAuthStore()
const toast = useToast()

const isDoctor = computed(() => !!auth.hasRole(UserRole.telemedicineDoctor))
const loading = ref(true)
const counts = ref({ published: 0, draft: 0, suspended: 0, specialties: 0 })
const enabled = ref(false)
const profile = ref<DoctorProfile>()

const statusColor = { draft: 'gray', published: 'green', suspended: 'red' } as const

const gaps = computed(() => (profile.value ? publishGaps(profile.value) : { doctor: [], team: [] }))

const stats = computed(() => [
    { label: 'Published doctors', value: counts.value.published },
    { label: 'Drafts', value: counts.value.draft },
    { label: 'Suspended', value: counts.value.suspended },
    { label: 'Active specialties', value: counts.value.specialties },
])

const roadmap = [
    { code: 'M2', text: 'Doctor availability and scheduling.' },
    { code: 'M3', text: 'Patient booking and payment in the app.' },
    { code: 'M4', text: 'Video consultations and notes.' },
    { code: 'M5', text: 'Payouts, reporting and launch.' },
]

async function loadDoctor() {
    const { data } = await client.from('doctor_profiles').select('*').eq('user_id', auth.user?.id as string).maybeSingle()
    profile.value = (data ?? undefined) as unknown as DoctorProfile | undefined
}

async function loadAdmin() {
    const [profiles, specialties, settings] = await Promise.all([
        client.from('doctor_profiles').select('status'),
        client.from('specialties').select('id', { count: 'exact', head: true }).eq('active', true),
        client.from('telemedicine_settings').select('telemedicine_enabled').maybeSingle(),
    ])
    const failed = [profiles, specialties, settings].find((r) => r.error)
    if (failed?.error) {
        toast.add({ title: 'Could not load telemedicine figures', description: failed.error.message, icon: 'i-ph-warning-duotone', color: 'red' })
        return
    }
    const rows = (profiles.data ?? []) as unknown as { status: DoctorStatus }[]
    counts.value = {
        published: rows.filter((r) => r.status === DoctorStatus.published).length,
        draft: rows.filter((r) => r.status === DoctorStatus.draft).length,
        suspended: rows.filter((r) => r.status === DoctorStatus.suspended).length,
        specialties: specialties.count ?? 0,
    }
    enabled.value = !!(settings.data as unknown as { telemedicine_enabled?: boolean } | null)?.telemedicine_enabled
}

onMounted(async () => {
    try {
        await (isDoctor.value ? loadDoctor() : loadAdmin())
    } finally {
        loading.value = false
    }
})
</script>
